/**
 * Utility for generating dynamic brand avatars and cover images
 * Creates professional-looking placeholders when brand images are missing
 */

// Professional color palette for brand avatars
const BRAND_COLORS = [
  '#1976d2', // Blue
  '#388e3c', // Green
  '#f57c00', // Orange
  '#7b1fa2', // Purple
  '#d32f2f', // Red
  '#0288d1', // Light Blue
  '#689f38', // Light Green
  '#f9a825', // Amber
  '#512da8', // Deep Purple
  '#c2185b', // Pink
  '#00796b', // Teal
  '#5d4037', // Brown
  '#455a64', // Blue Grey
  '#e64a19', // Deep Orange
  '#303f9f', // Indigo
];

/**
 * Generates a consistent color based on brand name
 */
export const getBrandColor = (brandName: string): string => {
  if (!brandName) return BRAND_COLORS[0];
  
  // Simple hash function to get consistent color for same brand name
  let hash = 0;
  for (let i = 0; i < brandName.length; i++) {
    const char = brandName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const index = Math.abs(hash) % BRAND_COLORS.length;
  return BRAND_COLORS[index];
};

/**
 * Gets the first letter of brand name for avatar
 */
export const getBrandInitial = (brandName: string): string => {
  if (!brandName) return '?';
  return brandName.charAt(0).toUpperCase();
};

/**
 * Generates a gradient for cover image based on brand color
 */
export const getBrandGradient = (brandName: string): string => {
  const baseColor = getBrandColor(brandName);
  
  // Create a subtle gradient by lightening the base color
  const lighterColor = adjustColorBrightness(baseColor, 30);
  
  return `linear-gradient(135deg, ${baseColor} 0%, ${lighterColor} 100%)`;
};

/**
 * Adjusts color brightness
 */
const adjustColorBrightness = (hex: string, percent: number): string => {
  // Remove # if present
  const color = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  
  // Adjust brightness
  const newR = Math.min(255, Math.max(0, r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.max(0, g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.max(0, b + (255 - b) * (percent / 100)));
  
  // Convert back to hex
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

/**
 * Checks if an image URL is valid (not null, undefined, or empty)
 */
export const isValidImageUrl = (url: string | null | undefined): boolean => {
  return Boolean(url && url.trim() !== '' && url !== 'null' && url !== 'undefined');
}; 